import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import {
  MagnifyingGlassIcon,
  ComputerDesktopIcon,
  ArrowPathIcon,
  UserIcon
} from '@heroicons/react/24/outline';

const OnlineUsers = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ online: [], offline: [], totalOnline: 0, totalOffline: 0 });
  const [searchTerm, setSearchTerm] = useState('');
  const [tabValue, setTabValue] = useState(0);

  const fetchOnlineUsers = async () => {
    try {
      const response = await api.get('/online/users');
      setData(response.data);
    } catch (error) {
      console.error('Online users fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOnlineUsers();
    const interval = setInterval(fetchOnlineUsers, 30000);
    return () => clearInterval(interval);
  }, []);

  const filteredUsers = (users) => {
    if (!searchTerm) return users;
    const term = searchTerm.toLowerCase();
    return users.filter(u =>
      u.fullName?.toLowerCase().includes(term) ||
      u.username?.toLowerCase().includes(term) ||
      u.department?.toLowerCase().includes(term)
    );
  };

  const UserCard = ({ user: u }) => (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 flex items-center gap-4">
      <div className="relative">
        <div className={`h-12 w-12 rounded-full flex items-center justify-center text-white font-bold ${u.isOnline ? 'bg-green-500' : 'bg-gray-400'}`}>
          {u.fullName?.charAt(0) || 'U'}
        </div>
        <span className={`absolute bottom-0 right-0 h-3 w-3 rounded-full border-2 border-white dark:border-gray-800 ${u.isOnline ? 'bg-green-500' : 'bg-red-500'}`}></span>
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="font-semibold text-gray-900 dark:text-white truncate">{u.fullName}</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400">@{u.username}</p>
        <div className="flex flex-wrap gap-2 mt-1">
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300">
            {u.department || 'Bilinmiyor'}
          </span>
          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${u.isOnline ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'}`}>
            <ComputerDesktopIcon className="h-3 w-3 mr-1" />
            {u.isOnline ? 'Desktop App' : 'Offline'}
          </span>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  const tabs = [
    { name: 'Online', count: filteredUsers(data.online).length, color: 'green' },
    { name: 'Offline', count: filteredUsers(data.offline).length, color: 'red' },
    { name: 'Tümü', count: filteredUsers([...data.online, ...data.offline]).length, color: 'gray' }
  ];

  return (
    <div>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          Desktop App Kullanıcıları
        </h1>
        <div className="flex items-center gap-3">
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300">
            <span className="h-2 w-2 rounded-full bg-green-500 mr-2"></span>
            {data.totalOnline} Online
          </span>
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300">
            <span className="h-2 w-2 rounded-full bg-red-500 mr-2"></span>
            {data.totalOffline} Offline
          </span>
          <button
            onClick={fetchOnlineUsers}
            className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
            title="Yenile"
          >
            <ArrowPathIcon className="h-5 w-5" />
          </button>
        </div>
      </div>

      <div className="relative mb-6">
        <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          placeholder="Kullanıcı ara..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
        />
      </div>

      <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab, index) => (
            <button
              key={tab.name}
              onClick={() => setTabValue(index)}
              className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
                tabValue === index
                  ? `border-${tab.color}-500 text-${tab.color}-600 dark:text-${tab.color}-400`
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400'
              }`}
            >
              <span className={`h-2 w-2 rounded-full ${tab.color === 'green' ? 'bg-green-500' : tab.color === 'red' ? 'bg-red-500' : 'bg-gray-500'}`}></span>
              {tab.name} ({tab.count})
            </button>
          ))}
        </nav>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {tabValue === 0 && filteredUsers(data.online).map(u => (
          <UserCard key={u.id} user={u} />
        ))}
        {tabValue === 1 && filteredUsers(data.offline).map(u => (
          <UserCard key={u.id} user={u} />
        ))}
        {tabValue === 2 && filteredUsers([...data.online, ...data.offline]).map(u => (
          <UserCard key={u.id} user={u} />
        ))}
      </div>

      {tabValue === 0 && filteredUsers(data.online).length === 0 && (
        <div className="text-center py-12">
          <ComputerDesktopIcon className="h-16 w-16 mx-auto text-gray-400 mb-4" />
          <p className="text-gray-500 dark:text-gray-400">
            Şu an online kullanıcı yok
          </p>
        </div>
      )}

      {tabValue === 1 && filteredUsers(data.offline).length === 0 && (
        <div className="text-center py-12">
          <UserIcon className="h-16 w-16 mx-auto text-gray-400 mb-4" />
          <p className="text-gray-500 dark:text-gray-400">
            Offline kullanıcı bulunamadı
          </p>
        </div>
      )}
    </div>
  );
};

export default OnlineUsers;
